import {
  useInfiniteQuery,
  useMutation,
  useQueryClient,
  useQuery,
} from '@tanstack/react-query';
import {
  getConversation,
  getConversationItems,
} from '../api/conversations';
import { sendMessage } from '../api/responses';
import type { Conversation, ConversationMessage } from '@/types/conversation';
import type { PaginatedItemsResponse, OpenAIResponseOutput } from '@/types/api';

// Query key factory
export const messageKeys = {
  metadata: (id: string) => ['conversation', id] as const,
  items: (id: string) => ['messages', id] as const,
};

/**
 * Fetch conversation metadata (no messages in v2)
 */
export function useConversationMetadata(conversationId: string | null) {
  return useQuery<Conversation>({
    queryKey: messageKeys.metadata(conversationId || ''),
    queryFn: () => getConversation(conversationId!),
    enabled: !!conversationId,
  });
}

/**
 * Fetch paginated messages using infinite query
 */
export function useMessages(conversationId: string | null) {
  return useInfiniteQuery<PaginatedItemsResponse>({
    queryKey: messageKeys.items(conversationId || ''),
    queryFn: ({ pageParam }) =>
      getConversationItems(conversationId!, {
        limit: 20,
        after: pageParam as string | undefined,
        order: 'desc', // Newest messages first, "load more" gets older messages
      }),
    initialPageParam: undefined as string | undefined,
    getNextPageParam: (lastPage) =>
      lastPage.hasMore ? lastPage.lastId : undefined,
    enabled: !!conversationId,
  });
}

/**
 * Helper hook that flattens paginated messages
 * Messages are fetched in desc order (newest first), reversed here for display (oldest first)
 */
export function useFlatMessages(conversationId: string | null) {
  const query = useMessages(conversationId);

  // Reverse messages for chronological display (oldest at top, newest at bottom)
  // Pages are in order: [newest page, ..., oldest page]
  // Each page contains messages in desc order: [newest in page, ..., oldest in page]
  // We need: [oldest overall, ..., newest overall]
  const messages: ConversationMessage[] =
    query.data?.pages.flatMap((page) => page.messages).reverse() || [];

  return {
    ...query,
    messages,
  };
}

// Type for infinite query data
type InfiniteMessagesData = {
  pages: PaginatedItemsResponse[];
  pageParams: (string | undefined)[];
};

// Context type for optimistic update rollback
type SendMessageContext = {
  previousData: InfiniteMessagesData | undefined;
};

/**
 * Send message mutation (non-streaming)
 */
export function useSendMessage() {
  const queryClient = useQueryClient();

  return useMutation<
    OpenAIResponseOutput,
    Error,
    { input: string; conversationId?: string },
    SendMessageContext
  >({
    mutationFn: ({ input, conversationId }) =>
      sendMessage(input, conversationId),

    onMutate: async ({ input, conversationId }): Promise<SendMessageContext> => {
      if (!conversationId) return { previousData: undefined };

      // Cancel outgoing refetches
      const queryKey = messageKeys.items(conversationId);
      await queryClient.cancelQueries({ queryKey });

      // Snapshot previous value
      const previousData =
        queryClient.getQueryData<InfiniteMessagesData>(queryKey);

      // Optimistically add user message to the first page (newest messages)
      // Since messages are fetched in desc order and reversed for display,
      // adding to the start of first page makes it appear at the bottom after reversal
      queryClient.setQueryData<InfiniteMessagesData>(queryKey, (old) => {
        if (!old) return old;

        const tempMessage: ConversationMessage = {
          id: `temp-${Date.now()}`,
          role: 'user',
          content: input,
          createdAt: new Date().toISOString(),
        };

        const newPages = [...old.pages];
        if (newPages.length > 0) {
          // Add to the START of the FIRST page (which contains newest messages)
          newPages[0] = {
            ...newPages[0],
            messages: [tempMessage, ...newPages[0].messages],
          };
        }

        return { ...old, pages: newPages };
      });

      return { previousData };
    },

    onError: (_err, { conversationId }, context) => {
      // Rollback on error
      if (conversationId && context?.previousData) {
        queryClient.setQueryData(
          messageKeys.items(conversationId),
          context.previousData
        );
      }
    },

    onSuccess: (data) => {
      // v2: data.conversation.id instead of data.conversationId
      const convId = data.conversation.id;

      // Invalidate to refetch fresh data
      queryClient.invalidateQueries({ queryKey: messageKeys.items(convId) });
      queryClient.invalidateQueries({ queryKey: ['conversations'] });
    },
  });
}
